<template>
  <div class="h-full w-full flex flex-col">
    <div id="loading-overlay" class="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50"
      v-if="isGenerateReport">
      <img src="../../assets/images/loading_spinner/1.gif" alt="Loading..." />
    </div>
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col min-ht-78">
          <div class="h-full flex justify-start items-center">
            <div v-if="token" class="flex-shrink-0 mr-4">
              <icon-back @click.stop="goBackTo('/post-event')"></icon-back>
            </div>
            <div class="flex-grow">
              <div class="flex items-center text-sm font-light">
                <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                <span class="mx-2">/</span>
                <router-link to="/post-event" class="opacity-50 hover:opacity-100">Events</router-link>
                <span class="mx-2">/</span>
                <span>Event Registrations</span>
              </div>
              <div class="font-bold">{{ getTitle }}</div>
            </div>
          </div>
        </div>

        <div v-if="hasItems" class="mt-4 flex justify-end items-center">
          <div v-if="isGenerateReport"
            class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg hover:text-ternary text-white">
            <base-spinner-with-text class-list="w-4 h-4" text="" text-class-list="text-white"></base-spinner-with-text>
            <span class="text-xs">Exporting Excel</span>
          </div>
          <div v-else @click="generateReport"
            class="w-44 flex items-center justify-center hover:bg-purple-50 bg-ternary border-ternary hover:border-ternary border px-4 py-2 rounded-lg cursor-pointer hover:text-ternary text-white">
            <span class="text-xs">Export Excel</span>
          </div>
        </div>

        <section :class="hasItems ? 'mt-4' : 'mt-8'"
          class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
          <div class="flex justify-between items-start flex-wrap">
            <h1 class="text-base md:text-lg font-semibold mb-4">
              {{ getSubTitle }}
            </h1>
            <div class="flex justify-start items-center flex-wrap mb-4">
              <div class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Local or Non Local:</div>
                <select v-model="filterByLocalOrNonLocal" class="outline-none text-sm flex-grow">
                  <option value="">All</option>
                  <option value="Local">Local</option>
                  <option value="Non Local">Non Local</option>
                </select>
              </div>
              <div class="w-full sm:w-auto mr-0 sm:mr-8 mb-4 border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Status:</div>
                <select v-model="filterByStatus" class="outline-none text-sm flex-grow">
                  <option value="">All</option>
                  <option :value="ss.code" v-for="(ss, index) in studentStatusList" :key="index">{{ ss.name }}</option>
                </select>
              </div>
              <div class="w-full sm:w-auto mb-4 border rounded-lg px-4 h-8 flex items-center">
                <div class="text-sm mr-2">Departments:</div>
                <select v-model="filterByDepartment" class="outline-none text-sm flex-grow">
                  <option value="">All</option>
                  <option :value="department" v-for="(department, index) in departments" :key="index">{{ department }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="flex flex-col" v-if="$apollo.queries.eventRegistrations.loading">
            <table-shimmer :titleHeaders="[
              '#',
              'Student',
              'Entity',
              'Event',
              'Registered At',
            ]" :rows="first"></table-shimmer>
          </div>
          <div v-else-if="!$apollo.queries.eventRegistrations.loading && !hasItems"
            class="h-full flex justify-center items-center">
            <h1 class="text-center text-xl font-medium">
              No Event Registrations found!
            </h1>
          </div>
          <div v-else class="flex flex-col -mt-4">
            <div class="flex flex-col overflow-x-auto">
              <div class="inline-block min-w-full py-2">
                <div class="overflow-x-auto">
                  <table class="min-w-full text-left text-sm font-light">
                    <thead class="border-b font-medium dark:border-neutral-500">
                      <tr>
                        <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                          #
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Student
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Entity
                        </th>
                        <th v-if="!hasToken" scope="col" class="px-6 py-2 text-gray-400">
                          Event
                        </th>
                        <th v-if="isShowCvSubmittedToColumn" scope="col" class="px-6 py-2 text-gray-400">
                          CV Submitted To
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Registered At
                        </th>
                        <th scope="col" class="px-6 py-2 text-gray-400">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in items" :key="index"
                        class="border-b border-dashed dark:border-neutral-500">
                        <td class="whitespace-nowrap px-6 py-2 font-medium">
                          {{ paginatorInfo.firstItem + index }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <div @click.stop="
                            viewStudentProfile(item.user.students.student_id)
                            " class="flex items-center cursor-pointer group">
                            <div class="flex-shrink-0 mr-3">
                              <img class="h-12 w-12 rounded-lg object-cover" :src="getProfilePicture(
                                item.user.profile_picture_url,
                                item.user.students.student_id,
                                item.user.students.entity
                              )
                                " alt="" />
                            </div>
                            <div class="flex-grow flex flex-col mr-4">
                              <h1 class="text-sm font-semibold flex items-center">
                                <span class="text-heading group-hover:text-blue-600">{{ item.user.name }}</span>
                              </h1>
                              <p class="text-xs text-gray-400 group-hover:text-blue-400">
                                {{ item.user.students.student_id }}
                              </p>
                              <p class="text-xs text-gray-400 group-hover:text-blue-400">
                                {{ item.user.email }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getEntityName(item.user.students.entity) }}
                        </td>
                        <td v-if="!hasToken" class="whitespace-nowrap px-6 py-2">
                          <div v-if="item.event.event_type == 'Normal'">
                            {{ item.event.title }}
                          </div>
                          <div v-else @click.stop="openViewRegisteredStudent(item)"
                            class="cursor-pointer hover:underline hover:text-blue-600"
                            title="Click to view more details">
                            {{ `${item.event.title} (Virtual Career Fair)` }}
                          </div>
                        </td>
                        <td v-if="isShowCvSubmittedToColumn" class="px-6 py-2">
                          {{ getEmployersList(item.user.event_employer_attachments) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          {{ getFormattedDateWithTimeAmPm(item.created_at) }}
                        </td>
                        <td class="whitespace-nowrap px-6 py-2">
                          <div @click.stop="openViewRegisteredStudent(item)"
                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                            <icon-eye
                              class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="mt-4">
              <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
            </div>
          </div>
        </section>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="showFullModal" title="Student Profile" @close="closeFullModal">
        <student-profile :student-id="studentId"></student-profile>
      </base-full-modal>
    </transition>

    <transition name="fade" mode="out-in">
      <base-full-modal v-if="isOpenViewRegisteredStudent" :title="viewRegisteredStudentTitle"
        @close="closeViewRegisteredStudent">
        <view-registered-student :item="selectedStudent"></view-registered-student>
      </base-full-modal>
    </transition>
  </div>
</template>

<script>
import IconBack from "../../components/icons/IconBack.vue";
import IconEye from "../../components/icons/IconEye.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import StudentProfile from "../student-profile/index.vue";
import { formattedDateWithTimeAmPm } from "../../helpers/utils";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import ViewRegisteredStudent from "./ViewRegisteredStudent.vue";
export default {
  name: "event-registrations",
  props: ["token"],
  components: {
    IconBack,
    IconEye,
    Pagination,
    TableShimmer,
    StudentProfile,
    ViewRegisteredStudent,
  },
  data() {
    return {
      eventRegistrations: [],
      first: 10,
      page: 1,
      showFullModal: false,
      studentId: null,
      filterByLocalOrNonLocal: "",
      filterByStatus: "",
      studentStatusList: [],
      departments: [],
      filterByDepartment: "",
      isGenerateReport: false,
      generatedReport: [],
      isOpenViewRegisteredStudent: false,
      viewRegisteredStudentTitle: "",
      selectedStudent: null,
    };
  },
  watch: {
    eventRegistrations(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByLocalOrNonLocal() {
      this.page = 1;
    },
    filterByStatus() {
      this.page = 1;
    },
    filterByDepartment() {
      this.page = 1;
    },
  },
  computed: {
    items() {
      return this.eventRegistrations.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.eventRegistrations.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    getTitle() {
      if (this.token) {
        return localStorage.getItem("eventRegistrationTitle");
      } else {
        return "Event Registrations";
      }
    },
    getSubTitle() {
      if (this.token) {
        if (this.$apollo.queries.eventRegistrations.loading) {
          return "";
        } else {
          return `Event Registrations List (${this.paginatorInfo.total})`;
        }
      } else {
        return "Event Registrations List";
      }
    },
    hasToken() {
      if (this.token !== undefined) {
        return true;
      } else {
        return false;
      }
    },
    isShowCvSubmittedToColumn() {
      return (
        this.hasToken &&
        this.hasItems &&
        this.items.length > 0 &&
        this.items[0] &&
        this.items[0].event &&
        this.items[0].event.event_type === 'Virtual Career Fair'
      );
    },
  },
  apollo: {
    studentStatusList() {
      return {
        query: gql`
          query GetStudentStatusList {
            studentStatusList(is_show: "1") {
              code
              name
            }
          }
        `,
      };
    },
    departments() {
      return {
        query: gql`
          query GetCollegeDepartments($token: String) {
            departments(token: $token)
          }
        `,
        variables() {
          return {
            token: this.token ? this.token : '',
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
    eventRegistrations() {
      return {
        query: gql`
          query GetAllEventRegistrations(
            $first: Int!
            $page: Int!
            $token: String
            $local_or_non_local: String
            $student_status: String
            $program_name: String
          ) {
            eventRegistrations(
              first: $first
              page: $page
              token: $token
              local_or_non_local: $local_or_non_local
              student_status: $student_status
              program_name: $program_name
            ) {
              error
              error_code
              message
              data {
                id
                user_id
                event_id
                created_at
                updated_at
                event {
                  event_type
                  title
                  location
                  focal_point
                  start_date
                  end_date
                  display_day
                  display_time
                  event_employers {
                    partner {
                      en_company_name
                    }
                  }
                }
                user {
                  name
                  email
                  profile_picture_url
                  students {
                    student_id
                    entity
                  }
                  event_employer_attachments {
                    path
                    event_employer {
                      partner {
                        en_company_name
                      }
                    }
                  }
                }
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
        variables() {
          return {
            first: this.first,
            page: this.page,
            token: this.token ? this.token : "",
            local_or_non_local: this.filterByLocalOrNonLocal,
            student_status: this.filterByStatus,
            program_name: this.filterByDepartment,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    getEntityName(entity) {
      return entity.replace(/ATHS/g, "ATS");
    },
    viewStudentProfile(studentId) {
      this.studentId = studentId;
      this.showFullModal = true;
    },
    closeFullModal() {
      this.showFullModal = false;
      this.studentId = null;
    },
    getProfilePicture(profilePictureUrl, studentId, entity) {
      if (
        profilePictureUrl ==
        "/storage/alumni-profile-pictures/default-avatar.jpg"
      ) {
        return `https://www.iat.ac.ae/sis/Integrations/Get_Student_Image/Q4tphjXBanCMiro8Kpfbi1QEU5qZb01uWtpkQlpfet8/${studentId}/${entity}`;
      } else {
        return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
      }
    },
    setPage(page) {
      this.page = page;
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    goBackTo(url) {
      this.$router.replace(url);
    },
    getFormattedDateWithTimeAmPm(date) {
      return formattedDateWithTimeAmPm(date);
    },
    async generateReport() {
      document.body.classList.add("loading");
      this.isGenerateReport = true;
      let error_msg = null;

      try {
        const { data } = await this.$apollo.query({
          query: gql`
            query GenerateEventRegistrationsReport(
              $token: String!
              $local_or_non_local: String
              $student_status: String
              $program_name: String
            ) {
              generateEventRegistrationsReport(
                token: $token
                local_or_non_local: $local_or_non_local
                student_status: $student_status
                program_name: $program_name
              ) {
                error
                error_code
                message
                data {
                  created_at
                  user {
                    name
                    email
                    students {
                      student_id
                      entity
                      alumni_phone
                      primary_phone
                      secondary_phone
                    }
                  }
                }
              }
            }
          `,
          variables: {
            token: this.token,
            local_or_non_local: this.filterByLocalOrNonLocal,
            student_status: this.filterByStatus,
            program_name: this.filterByDepartment,
          },
          fetchPolicy: "network-only",
        });

        this.generatedReport = data.generateEventRegistrationsReport.data.map(
          (obj) => {
            return {
              student_id: obj.user.students.student_id,
              name: obj.user.name,
              email: obj.user.email,
              alumni_phone: obj.user.students.alumni_phone,
              primary_phone: obj.user.students.primary_phone,
              secondary_phone: obj.user.students.secondary_phone,
              entity:
                obj.user.students.entity == "ATHS"
                  ? "ATS"
                  : obj.user.students.entity,
              created_at: this.getFormattedDateWithTimeAmPm(obj.created_at),
            };
          }
        );
      } catch (error) {
        console.log(error.message);
      }

      if (this.generatedReport.length > 0) {
        await this.exportExcel(localStorage.getItem("eventRegistrationTitle"));
      } else {
        error_msg = "No records to generate the report";
        this.$swal
          .fire({
            icon: "error",
            title: "Oops...",
            text: error_msg,
          })
          .then((result) => {
            if (result.value || result.dismiss) {
              error_msg = null;
            }
          });
      }

      this.isGenerateReport = false;
      document.body.classList.remove("loading");
      this.generatedReport = [];
    },
    async exportExcel(reportName) {
      const XLSX = xlsx;
      const header = [
        "STUDENT ID",
        "NAME",
        "EMAIL",
        "ALUMNI PHONE",
        "PRIMARY PHONE",
        "SECONDARY PHONE",
        "ENTITY",
        "REGISTERED AT",
      ];
      const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
      worksheet["A1"] = { v: header[0], t: "s" };
      worksheet["B1"] = { v: header[1], t: "s" };
      worksheet["C1"] = { v: header[2], t: "s" };
      worksheet["D1"] = { v: header[3], t: "s" };
      worksheet["E1"] = { v: header[4], t: "s" };
      worksheet["F1"] = { v: header[5], t: "s" };
      worksheet["G1"] = { v: header[6], t: "s" };
      worksheet["H1"] = { v: header[7], t: "s" };

      worksheet["!cols"] = [
        { width: 20 },
        { width: 60 },
        { width: 60 },
        { width: 30 },
        { width: 30 },
        { width: 30 },
        { width: 20 },
        { width: 30 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, `${reportName}.xlsx`);
    },
    getEmployersList(eventEmployerAttachments) {
      return eventEmployerAttachments.map(a => a.event_employer.partner.en_company_name).join(', ');
    },
    openViewRegisteredStudent(eventRegObj) {
      this.selectedStudent = eventRegObj;
      this.viewRegisteredStudentTitle = `${eventRegObj.event.title} - ${eventRegObj.user.name}`;
      this.isOpenViewRegisteredStudent = true;
    },
    closeViewRegisteredStudent() {
      this.isOpenViewRegisteredStudent = false;
      this.viewRegisteredStudentTitle = "";
      this.selectedStudent = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@responsive {
  .min-ht-78 {
    min-height: 78px;
  }
}
</style>
